import './GoalHeader.scss';

import FallbackBackground from '../../../../assets/images/goal-fallback-banner.jpg';
import getClubLogo from '../../../../utils/getClubLogo';
import { useCallback, useEffect, useState } from 'react';
import { THEOplayerView } from '../player/THEOplayerView';
import { useSearchParams } from 'react-router-dom';
import {
    bufferToHex,
    generateUniqueScor,
    getCookie,
    transformCustomParams,
    transformParams
} from './functions';

const competitionMap = {
    9: 'eredivisie',
    96: 'keukenkampioendivisie',
    395: 'knvbbeker',
    923: 'vrouweneredivisie',
    1116: 'vrouwenknvbbeker',
    18: 'johancruijffschaal',
    485: 'keukenkampioendivisieplayoffs',
    _1: 'playoffseuropeesvoetbal'
};

const fetchIp = async setHip => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');

        const data = await response.json();

        const ip = data.ip;

        const saltedIp = `ip${ip}`;

        const encoder = new TextEncoder();
        const dataToHash = encoder.encode(saltedIp);

        const hashBuffer = await crypto.subtle.digest('SHA-256', dataToHash);
        const hashedIp = bufferToHex(hashBuffer);

        setHip(hashedIp);
    } catch (error) {
        console.error('Error fetching the IP address:', error);
    }
};

const createAdUrl = (
    base,
    hip,
    isHighlightVideo,
    matchId,
    mediaId,
    competition
) => {
    const matchedCompetition = competition ? competitionMap[competition] : null;
    const customParams = {
        vdm: 'vod',
        ait: 'csai',
        beacTyp: 'csai',
        isAutoplay: 0,
        isMute: 1,
        var: '16x9',
        vps: '1280x720',
        hip,
        uni: 'f',
        type: isHighlightVideo ? 'highlight' : 'goal'
    };

    const scor = generateUniqueScor();

    const url = {
        description_url: `http://espn.goal.webplayer.nl/goal/video/${matchId}/${mediaId}`,
        is_lat: 0,
        gdpr: 1,
        gdpr_consent: getCookie('eupubconsent-v2'),
        url: `http://espn.goal.webplayer.nl/goal/video/${matchId}/${mediaId}`,
        pp: 'espn-app-clips-web-csai',
        scor
    };

    const params = transformParams(url);

    const customEncodedParams = encodeURIComponent(
        transformCustomParams(customParams)
    );

    const adUrl = `${base}&${params}&cust_params=${customEncodedParams}&lg=${matchedCompetition}`;

    return adUrl;
};

const GoalHeader = ({ data }) => {
    const [hip, setHip] = useState('');
    const [params] = useSearchParams();
    const matchId = params.get('matchId');
    const mediaId = params.get('mediaId');
    const {
        adUrl,
        videoType,
        videoUrl,
        background,
        title,
        teams,
        gameMinute,
        date,
        competition
    } = data;

    const isHighlightVideo = videoType === 'Highlights';

    useEffect(() => {
        fetchIp(setHip);
    }, []);

    const onPlayer = useCallback(
        player => {
            player.autoplay = 0;
            player.muted = 1;

            player.source = {
                sources: [
                    {
                        src: videoUrl
                    }
                ],
                ads: [
                    {
                        sources: createAdUrl(
                            adUrl,
                            hip,
                            isHighlightVideo,
                            matchId,
                            mediaId,
                            competition
                        ),

                        type: 'vmap',
                        integration: 'google-ima'
                    }
                ]
            };
        },
        [hip, matchId, mediaId, videoUrl, isHighlightVideo]
    );

    const headerStyle = {
        backgroundImage: background
            ? `linear-gradient(to right, #000, #0000 100%), url(${background})`
            : `linear-gradient(11.33deg, rgba(0, 0, 0, 0.78) 30.32%, rgba(0, 0, 0, 0) 98.84%, rgba(0, 0, 0, 0.7) 112.99%), url(${FallbackBackground})`
    };

    const containerStyle = isHighlightVideo
        ? 'container container__video'
        : 'container container__video container__goal';

    const innerContainerStyle = isHighlightVideo
        ? 'container--inner container--inner__video'
        : 'container--inner';

    const videoContainerStyle = isHighlightVideo
        ? 'video-container'
        : 'video-container video-container__goal';

    const clubLogo = (url, name) => {
        if (url?.includes('undefined')) {
            return getClubLogo(name);
        }
        return url;
    };

    return (
        <header className="header__goalvideo" style={headerStyle}>
            <div className={containerStyle}>
                <div className={innerContainerStyle}>
                    <div className="logo-container">
                        <div className="team-wrapper">
                            <img
                                src={clubLogo(
                                    teams?.home?.logo?.smallUrl,
                                    teams?.home?.name
                                )}
                                alt={teams?.home?.name}
                            />
                            <h4 className="heading heading--small heading--white">
                                {teams?.home?.name}
                            </h4>
                        </div>
                        <h3 className="goal heading heading--custom heading--white">
                            {isHighlightVideo ? 'Samenvatting' : 'GOAL!'}
                        </h3>

                        <div className="team-wrapper">
                            <img
                                src={clubLogo(
                                    teams?.away?.logo?.smallUrl,
                                    teams?.away?.name
                                )}
                                alt={teams?.away?.name}
                            />
                            <h4 className="heading heading--small heading--white">
                                {teams?.away?.name}
                            </h4>
                        </div>
                    </div>
                    <h1 className="title heading heading--small heading--white ">
                        {title}
                    </h1>

                    {!isHighlightVideo && date && (
                        <div className="meta-container">
                            {!isHighlightVideo && gameMinute && (
                                <span>{gameMinute}'</span>
                            )}
                            {!isHighlightVideo && gameMinute && date && (
                                <span> | </span>
                            )}
                            {date && <span>{date}</span>}
                        </div>
                    )}
                </div>

                {videoUrl && (
                    <>
                        <THEOplayerView
                            onPlayer={onPlayer}
                            className={videoContainerStyle}
                        />
                    </>
                )}
            </div>
        </header>
    );
};

export default GoalHeader;
